import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { Loader2, Mail, Lock, Eye, EyeOff, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import InlineEmailVerification from '../auth/InlineEmailVerification';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle, resetPassword, logout, currentUser } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  // Verification State
  const [needsVerification, setNeedsVerification] = useState(false);

  // Reset state when modal opens/closes or mode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError(null);
      setResetSent(false);

      // If a user is already logged in but not verified, show verification immediately
      if (currentUser && !currentUser.emailVerified) {
        const isGoogleUser = currentUser.providerData?.some(
          (provider) => provider.providerId === 'google.com'
        );
        if (!isGoogleUser) {
          setNeedsVerification(true);
        }
      } else {
        setNeedsVerification(false);
      }
    }
  }, [isOpen, initialMode, currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (mode === 'login') {
        await signInWithEmail(email, password);
        // AuthContext / App.tsx will handle redirecting to VerifyEmailPrompt 
        // if they aren't verified, but we'll try to catch it here first
        // Note: currentUser won't update immediately here, handled in useEffect
      } else {
        await signUpWithEmail(email, password, displayName);
        // After signup, AuthContext will set currentUser
        // We catch it in the useEffect below
      }
    } catch (err: any) {
      // Czech error messages
      if (err.code === 'auth/user-not-found') {
        setError('Uživatel s tímto emailem neexistuje.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Nesprávné heslo.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('Tento email je již registrován.');
      } else if (err.code === 'auth/weak-password') {
        setError('Heslo musí mít alespoň 6 znaků.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Neplatný email.');
      } else {
        setError('Nastala chyba. Zkuste to prosím znovu.');
      }
      setIsLoading(false);
    }
  };

  // Watch for current user changes to determine if we should show verification or close
  useEffect(() => {
    if (!isOpen || !isLoading || !currentUser) return;

    const isGoogleUser = currentUser.providerData?.some(
      (provider) => provider.providerId === 'google.com'
    );

    if (!currentUser.emailVerified && !isGoogleUser) {
      setNeedsVerification(true);
      setIsLoading(false);
    } else {
      // Verified or Google user -> success
      onClose();
      setIsLoading(false);
    }
  }, [currentUser, isOpen, isLoading, onClose]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
      // Auth is handled, useEffect will close modal
    } catch (err) {
      setError('Přihlášení pomocí Google se nezdařilo.');
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError('Zadejte prosím váš email.');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (err) {
      setError('Nepodařilo se odeslat email pro obnovení hesla.');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
    setResetSent(false);
  };

  const handleVerificationComplete = () => {
    setNeedsVerification(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={() => !needsVerification ? onClose() : null}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="relative w-full max-w-md bg-[#131f24] rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button - hide if verifying to force them to verify or logout */}
              {!needsVerification && (
                <button
                  onClick={onClose}
                  className="absolute top-4 left-4 p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}

              {/* Header */}
              <div className="pt-8 pb-4 text-center">
                <h2 className="text-2xl font-bold text-white">
                  {needsVerification
                    ? 'Téměř hotovo!'
                    : mode === 'login' ? 'Přihlásit se' : 'Vytvořit účet'
                  }
                </h2>
              </div>

              {/* Body */}
              <div className="px-8 pb-8">
                {needsVerification ? (
                  <>
                    <InlineEmailVerification theme="dark" onVerified={handleVerificationComplete} />
                    <button
                      onClick={async () => {
                        await logout();
                        setNeedsVerification(false);
                      }}
                      className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      Použít jiný účet
                    </button>
                  </>
                ) : (
                  <>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Display Name (only for signup) */}
                      {mode === 'signup' && (
                        <div className="relative">
                          <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Vaše jméno"
                            className="w-full bg-[#1f2937] text-white placeholder-gray-500 rounded-xl px-4 py-4 border-2 border-transparent focus:border-[#1cb0f6] focus:outline-none transition-colors"
                            required
                          />
                        </div>
                      )}

                      {/* Email */}
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email"
                          className="w-full bg-[#1f2937] text-white placeholder-gray-500 rounded-xl px-12 py-4 border-2 border-transparent focus:border-[#1cb0f6] focus:outline-none transition-colors"
                          required
                        />
                      </div>

                      {/* Password */}
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Heslo"
                          className="w-full bg-[#1f2937] text-white placeholder-gray-500 rounded-xl px-12 py-4 border-2 border-transparent focus:border-[#1cb0f6] focus:outline-none transition-colors"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Forgot Password (only for login) */}
                      {mode === 'login' && (
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={handleResetPassword}
                            className="text-sm text-gray-400 hover:text-[#1cb0f6] transition-colors"
                          >
                            Zapomněli jste heslo?
                          </button>
                        </div>
                      )}

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#1cb0f6] hover:bg-[#1cb0f6]/90 text-white font-bold uppercase tracking-wide rounded-xl px-8 py-4 shadow-[0_4px_0_#1899d6] active:shadow-none active:translate-y-[4px] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                        ) : (
                          mode === 'login' ? 'Přihlásit se' : 'Vytvořit účet'
                        )}
                      </button>
                    </form>

                    {/* Password Reset Success Message */}
                    {resetSent && (
                      <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 text-sm text-center">
                        Email pro obnovení hesla byl odeslán.
                      </div>
                    )}

                    {/* Error Message */}
                    {error && !resetSent && (
                      <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm text-center">
                        {error}
                      </div>
                    )}

                    {/* Divider */}
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-700"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[#131f24] px-4 text-sm text-gray-500">NEBO</span>
                      </div>
                    </div>

                    {/* Google Button */}
                    <button
                      onClick={handleGoogleSignIn}
                      disabled={isLoading}
                      className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl px-4 py-4 border-2 border-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      <span>Pokračovat s Google</span>
                    </button>

                    {/* Switch Mode */}
                    <div className="mt-6 text-center">
                      <span className="text-gray-400">
                        {mode === 'login' ? 'Nemáte účet? ' : 'Máte už účet? '}
                      </span>
                      <button
                        type="button"
                        onClick={switchMode}
                        className="text-[#1cb0f6] font-semibold hover:underline"
                      >
                        {mode === 'login' ? 'Vytvořit účet' : 'Přihlásit se'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              {!needsVerification && (
                <div className="bg-[#0f1720] px-8 py-4 text-center">
                  <p className="text-xs text-gray-500">
                    Přihlášením souhlasíte s{' '}
                    <button className="text-gray-400 hover:text-white underline">Podmínkami použití</button>
                    {' '}a{' '}
                    <button className="text-gray-400 hover:text-white underline">Ochranou soukromí</button>
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
